interface CodeGeneratorOptions {
  alphabet?: string;
  length?: number;
  salt?: number;
}

export class CodeGenerator {
  private readonly alphabet: string;
  private readonly length: number;
  private readonly salt: number;
  private readonly base: number;
  private counter = BigInt(0);

  constructor(options?: CodeGeneratorOptions) {
    this.alphabet = options?.alphabet ?? '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    this.length = Math.max(options?.length ?? 8, 8);
    this.base = this.alphabet.length;
    this.salt = options?.salt ?? 12;
  }

  public generate(): string {
    // Create unique number from timestamp and incrementing counter
    const timestamp = BigInt(Date.now());
    const uniqueNumber = (timestamp << BigInt(32)) | this.counter++;

    // Convert to base (alphabet length)
    const numbers: number[] = [];
    let remaining = uniqueNumber;
    let sum = this.salt;

    // Generate digits for unique number
    while (remaining > 0n && numbers.length < this.length - 1) {
      const digit = Number(remaining % BigInt(this.base));
      numbers.unshift(digit);
      sum += digit;
      remaining = remaining / BigInt(this.base);
    }

    // Pad with random numbers
    /* c8 ignore start */
    while (numbers.length < this.length - 1) {
      const randomDigit = this.getRandomInt(0, this.base - 1);
      numbers.push(randomDigit);
      sum += randomDigit;
    }
    /* c8 ignore end */

    // Add checksum
    numbers.push(sum % this.base);

    return numbers.map((num) => this.alphabet[num]).join('');
  }

  public verify(code: string): boolean {
    if (!code?.length || code.length !== this.length) {
      return false;
    }

    const codeValue = code.toUpperCase();
    const alphabetValue = this.alphabet.toUpperCase();

    const numbers: number[] = [];
    for (const char of codeValue) {
      const index = alphabetValue.indexOf(char);
      if (index === -1) return false;
      numbers.push(index);
    }

    const checksum = numbers.pop();
    const sum = numbers.reduce((acc, curr) => acc + curr, 0) + this.salt;
    return sum % this.base === checksum;
  }

  public generateBatch(amount: number): string[] {
    return Array.from({ length: amount }, () => this.generate());
  }

  private getRandomInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
}
